import {
  ColorMatrixFilter,
  Container,
  Graphics,
  MeshPlane,
  MeshRope,
  Point,
  Rectangle,
  Sprite,
  Texture,
} from 'pixi.js';
import {
  FrameWatch,
  SPLASH_SECONDS,
  SWAY_REPEAT,
  beamAt,
  birdsAt,
  cloudAt,
  cycleAt,
  dropsAt,
  flitsAt,
  followPath,
  grow,
  hash01,
  idleBob,
  idlePose,
  jointWeights,
  leapAt,
  poseLevel,
  leapCrossing,
  leapsAt,
  legSwing,
  lightLevel,
  lockedMatrix,
  overlaps,
  puffsAt,
  railCarsAt,
  serpentAt,
  serpentBody,
  sparklesAt,
  streaksAt,
  surfaceCrossings,
  trainLap,
  walkersAt,
  wave,
  windOf,
  type AmbientSpec,
  type BeamElement,
  type CloudElement,
  type FlitElement,
  type FlockElement,
  type FlowElement,
  type IdleElement,
  type Joint,
  type LeapElement,
  type LightsElement,
  type Part,
  type PathElement,
  type PlumeElement,
  type RailSpec,
  type Rect,
  type RigElement,
  type SerpentElement,
  type SparkleElement,
  type SprayElement,
  type SwayElement,
  type TrainElement,
  type WaterSpec,
  type Waterline,
} from '../../logic/realm/ambient';
import { SeaRippleFilter, rippleTexture } from '../../effects/SeaRippleFilter';
import { SwayFilter } from '../../effects/SwayFilter';

/** The loaded art for the ambient layer. */
export interface AmbientArt {
  spec: AmbientSpec;
  /** The sheets' frames, by name. */
  frames: Record<string, Texture>;
  /** Each water stretch's mask, by water id, and the trees' (sway) masks,
   *  by element id. */
  masks: Record<string, Texture>;
}

/** How the sea moves: `ripple` (a filter pass over the sea on screen),
 *  `highlights` (drifting wave highlights, sprites only, for slow
 *  devices), or `auto`: the ripple, falling back to the highlights for
 *  good when the frame rate drops. */
export type SeaMode = 'auto' | 'ripple' | 'highlights';

export interface AmbientOptions {
  /** Reduced motion: every piece sits still at its resting pose, with no
   *  birds, no turning beam, no moving sea and no swaying trees. */
  still: boolean;
  /** Where the ambient clock starts, in seconds (tests and recordings). */
  start?: number;
  /** The sea (default `auto`). */
  sea?: SeaMode;
  /** Where the moving copies of the painting go (the sea's ripple, the
   *  trees' sway): over the tiles, under anything else the scene draws on
   *  the painting (a world map's road lights). By default, the bottom of the
   *  ambient layer. */
  under?: Container;
}

export interface AmbientStats {
  /** Elements on screen and running. */
  running: number;
  total: number;
  paused: boolean;
  still: boolean;
  /** The ambient clock, in seconds. */
  clock: number;
  /** Time spent moving things, per frame, in milliseconds (a running
   *  average), for the frame-rate checks. */
  updateMs: number;
  /** How the sea moves now. */
  sea: 'ripple' | 'highlights' | 'none';
  /** Whether the trees sway now. */
  sway: 'on' | 'off' | 'none';
  /** The ripple gave way to the highlights, and the sway went (this time,
   *  or remembered from an earlier visit on this device). */
  fellBack: boolean;
  /** The frame watch's last median frame, milliseconds (0 before one). */
  frameMs: number;
}

/** One piece as the browser tests see it (see {@link RealmAmbient.pieces}). */
export interface PieceState {
  id: string;
  kind: string;
  /** The world map slot whose building it belongs to, or null. */
  slot: number | null;
  /** On screen and running (or shown, in reduced motion). */
  shown: boolean;
  /** Drawn at no strength: a light on a locked building, say. */
  dark: boolean;
  /** Everything that makes up how it looks now, as one string: equal
   *  strings are the same picture. */
  pose: string;
}

/** One moving thing on the map. */
interface Runner {
  readonly area: Rect;
  readonly root: Container;
  /** Drawn in the sky's shadow layer, under every cloud. */
  readonly shadow?: Container | null;
  /** Below this zoom it shows at rest and doesn't run. */
  readonly minZoom?: number;
  update(t: number, zoom: number): void;
  /** Back to rest (zoomed out below `minZoom`). */
  settle?(): void;
  /** The world map slot whose building it belongs to, if any. */
  readonly slot?: number;
  /** That building is locked to this level (1 locked, 0 open). */
  setLocked?(level: number): void;
  destroy(): void;
}

/** A light, a sparkle or a butterfly on a locked building: gone while it's
 *  locked, back as it opens. */
function fadeLocked(root: Container, level: number): void {
  root.alpha = 1 - Math.max(0, Math.min(1, level));
  root.renderable = root.alpha > 0.001;
}

/** A creature or vehicle on a locked building: grey and dim with it (the same matrix as the veil), colour
 *  coming back as it opens. One filter per picture, made when first needed. */
const GREYS = new WeakMap<Container, ColorMatrixFilter>();
function greyLocked(target: Container, level: number): void {
  if (level <= 0.001) {
    target.filters = null;
    return;
  }
  let grey = GREYS.get(target);
  if (!grey) {
    grey = new ColorMatrixFilter();
    // At the screen's own resolution: a filter draws at 1x by default.
    grey.resolution = 'inherit';
    GREYS.set(target, grey);
  }
  grey.matrix = lockedMatrix(level) as ColorMatrixFilter['matrix'];
  target.filters = [grey];
}

/** How a piece looks now, as one string: each part's place, size, strength
 *  and picture, and a checksum of any mesh's points (the pieces that bend). */
function poseOf(node: Container): string {
  const parts: string[] = [];
  const walk = (c: Container): void => {
    const sprite = c as Container & { texture?: { uid?: number }; geometry?: unknown };
    let sum = 0;
    const geometry = sprite.geometry as
      | { getBuffer?: (n: string) => { data?: ArrayLike<number> } | undefined }
      | undefined;
    const data = geometry?.getBuffer?.('aPosition')?.data;
    if (data) for (let i = 0; i < data.length; i++) sum += (data[i] ?? 0) * (1 + (i % 7));
    parts.push(
      [
        c.x.toFixed(2),
        c.y.toFixed(2),
        c.rotation.toFixed(3),
        c.scale.x.toFixed(3),
        c.scale.y.toFixed(3),
        c.alpha.toFixed(3),
        c.visible ? 1 : 0,
        c.renderable ? 1 : 0,
        sprite.texture?.uid ?? '',
        sum.toFixed(2),
      ].join(','),
    );
    for (const child of c.children) walk(child);
  };
  walk(node);
  return parts.join('|');
}

/** The longest step the clock takes, so a hitch doesn't make things jump. */
const MAX_STEP_MS = 100;
/** Room kept round the screen, in screen pixels, so things start moving
 *  just before they come into view. */
const VIEW_MARGIN = 80;
/** The clock time shown in reduced motion: the serpent is up. */
const REST_TIME = 6;
/** Remembers, per device, that the ripple was too slow (a timestamp). */
const SLOW_SEA_KEY = 'goodtime.realm.slowSea';
/** How long that's remembered before the ripple is tried again. */
const SLOW_SEA_DAYS = 7;

function rememberedSlowSea(): boolean {
  try {
    const at = Number(window.localStorage.getItem(SLOW_SEA_KEY));
    return at > 0 && Date.now() - at < SLOW_SEA_DAYS * 86_400_000;
  } catch {
    return false;
  }
}

function rememberSlowSea(): void {
  try {
    window.localStorage.setItem(SLOW_SEA_KEY, String(Date.now()));
  } catch {
    // Private mode or storage blocked: it falls back again next time.
  }
}

/**
 * The realm map's ambient life (flag `realm_alive`), drawn in the scene's
 * ambient layer in master pixels: the sea moving all round the island,
 * ships and boats rocking and sailing, gulls over every coast, the sea
 * serpent, the lighthouse beam over the sea, smoke and steam, the
 * fountains, and clouds drifting with their shadows; lights, flags, the
 * rivers, the caravan, the cart and the knights; and the life on land:
 * animals grazing, villagers waving and walking, butterflies and
 * sparkles. A world map's life is built the same way, with its trees
 * swaying too. Built from ambient.json; the motion maths is
 * logic/realm/ambient.
 *
 * Only what's on screen runs, and the small life holds still when zoomed
 * too far out to see it move; nothing runs while paused (the map is
 * hidden or covered); reduced motion shows every piece still. The sea's
 * ripple gives way to drifting highlights, and the trees stop swaying,
 * when the frame rate drops.
 */
export class RealmAmbient {
  private clock: number;
  private paused = false;
  private readonly still: boolean;
  private readonly runners: Runner[] = [];
  /** Each runner's element id and kind, in step with `runners`. */
  private readonly named: { id: string; kind: string }[] = [];
  private readonly waters: WaterRunner[] = [];
  private readonly sways: SwayRunner[] = [];
  private readonly skyShadows = new Container();
  private readonly sky = new Container();
  private running = 0;
  private updateMs = 0;
  private readonly glow: Texture;
  private readonly drop: Texture;
  private readonly stroke: Texture;
  private readonly star: Texture;
  /** Runners zoomed out below their `minZoom`, settled at rest. */
  private readonly settled = new Set<Runner>();
  private readonly watch = new FrameWatch();
  private seaMode: 'ripple' | 'highlights';
  private readonly autoSea: boolean;
  private fellBack = false;

  constructor(
    private readonly layer: Container,
    art: AmbientArt,
    opts: AmbientOptions,
  ) {
    this.still = opts.still;
    this.clock = opts.start ?? (opts.still ? REST_TIME : 0);
    // Nothing in the layer takes taps: the buildings and names over it do.
    layer.eventMode = 'none';
    this.glow = softDot();
    this.drop = dropTexture();
    this.stroke = strokeTexture();
    this.star = starTexture();
    const sea = opts.sea ?? 'auto';
    this.autoSea = sea === 'auto';
    this.fellBack = this.autoSea && rememberedSlowSea();
    this.seaMode = sea === 'highlights' || this.fellBack ? 'highlights' : 'ripple';
    if (!this.still) {
      const under = opts.under ?? layer;
      under.eventMode = 'none';
      for (const w of art.spec.water) {
        const mask = art.masks[w.id];
        if (mask) this.waters.push(new WaterRunner(layer, under, w, mask, this.stroke, this.seaMode));
      }
      for (const e of art.spec.elements) {
        const mask = e.kind === 'sway' ? art.masks[e.id] : undefined;
        if (e.kind !== 'sway' || !mask) continue;
        const sway = new SwayRunner(layer, under, e, mask);
        if (this.fellBack) sway.setOn(false);
        this.sways.push(sway);
      }
    }
    const frame = (name: string): Texture => art.frames[name] ?? Texture.EMPTY;
    const still = this.still;
    for (const e of art.spec.elements) {
      let r: Runner | null = null;
      if (e.kind === 'sway') continue;
      if (e.kind === 'rig' || e.kind === 'path') r = new BoatRunner(e, frame, this.glow, still);
      else if (e.kind === 'flock') r = still ? null : new FlockRunner(e, frame, this.glow);
      else if (e.kind === 'serpent') r = new SerpentRunner(e, frame);
      else if (e.kind === 'beam') r = new BeamRunner(e, frame, this.glow, still);
      else if (e.kind === 'cloud') r = new CloudRunner(e, frame);
      else if (e.kind === 'plume') r = new PlumeRunner(e, frame);
      else if (e.kind === 'spray') r = new SprayRunner(e, this.drop);
      else if (e.kind === 'train') r = isRailTrain(e) ? new RailRunner(e, frame, still) : new TrainRunner(e, frame, this.glow, still);
      else if (e.kind === 'leap') r = new LeapRunner(e, frame, still);
      else if (e.kind === 'lights') r = new LightsRunner(e, frame, this.glow, still);
      else if (e.kind === 'flow') r = still ? null : new FlowRunner(e, this.stroke);
      else if (e.kind === 'idle') r = new IdleRunner(e, frame, this.glow, still);
      else if (e.kind === 'flit') r = still ? null : new FlitRunner(e, frame);
      else if (e.kind === 'sparkle') r = still ? null : new SparkleRunner(e, this.star);
      if (!r) continue;
      if (r.shadow) {
        this.skyShadows.addChild(r.shadow);
        this.sky.addChild(r.root);
      } else {
        layer.addChild(r.root);
      }
      this.runners.push(r);
      this.named.push({ id: e.id, kind: e.kind });
    }
    // The sky goes over everything else: shadows, then the clouds.
    layer.addChild(this.skyShadows, this.sky);
    for (const r of this.runners) r.update(this.clock, 1);
  }

  /** Advance the clock and move what's on screen. `view` is the part of
   *  the painting on screen, in master pixels. */
  update(ms: number, view: Rect, zoom: number): void {
    if (this.paused) return;
    const t0 = performance.now();
    if (!this.still) this.clock += Math.min(ms, MAX_STEP_MS) / 1000;
    const near = grow(view, VIEW_MARGIN / Math.max(zoom, 1e-3));
    let running = 0;
    for (const r of this.runners) {
      const on = overlaps(r.area, near);
      r.root.visible = on;
      if (r.shadow) r.shadow.visible = on;
      if (!on) continue;
      if (this.still) {
        running++;
        continue;
      }
      // Too small to see at this zoom: shown at rest, not run.
      if (r.minZoom !== undefined && zoom < r.minZoom) {
        if (!this.settled.has(r)) {
          r.settle?.();
          this.settled.add(r);
        }
        continue;
      }
      this.settled.delete(r);
      running++;
      r.update(this.clock, zoom);
    }
    let rippling = false;
    // The sea only where it's seen: no margin, so an all-land view skips
    // the ripple's pass altogether.
    for (const w of this.waters) {
      const on = w.update(this.clock, zoom, view);
      if (on) {
        running++;
        rippling ||= this.seaMode === 'ripple';
      }
    }
    // The trees likewise.
    for (const w of this.sways) {
      if (w.update(this.clock, zoom, view)) {
        running++;
        rippling = true;
      }
    }
    // The frame-rate watch: only while the sea ripples or the trees sway
    // on screen.
    if (rippling && this.autoSea) {
      if (this.watch.frame(ms)) this.fallBack();
    } else {
      this.watch.reset();
    }
    this.running = running;
    this.updateMs += (performance.now() - t0 - this.updateMs) * 0.05;
  }

  /** The ripple (or the sway) is too slow here: drifting highlights and
   *  still trees from now on, and on this device's next visits for a while. */
  private fallBack(): void {
    this.seaMode = 'highlights';
    this.fellBack = true;
    for (const w of this.waters) w.setMode('highlights');
    for (const w of this.sways) w.setOn(false);
    rememberSlowSea();
    console.info('[RealmAmbient] the sea ripple was slow here: drifting highlights instead');
  }

  /** Switch the sea by hand (the frame-rate checks). */
  setSea(mode: 'ripple' | 'highlights'): void {
    this.seaMode = mode;
    for (const w of this.waters) w.setMode(mode);
    this.watch.reset();
  }

  /**
   * A world map's locked buildings, by slot (node id) to level (1 locked, 0
   * open): the life that belongs to each (its `slot`) goes dark or grey with
   * it, and comes back as colour flows back into the building.
   */
  setLocks(levels: ReadonlyMap<number, number>): void {
    for (const r of this.runners) {
      if (r.slot !== undefined) r.setLocked?.(levels.get(r.slot) ?? 0);
    }
  }

  /** Switch the trees' sway on or off by hand (the frame-rate checks). */
  setSway(on: boolean): void {
    for (const w of this.sways) w.setOn(on);
    this.watch.reset();
  }

  /** Stop everything (the map is hidden or covered), or carry on. */
  setPaused(paused: boolean): void {
    this.paused = paused;
    if (paused) this.watch.reset();
  }

  /** Keep the sea's copy of the map tiles in step with the scene's. */
  syncTiles(tiles: ReadonlyMap<string, Sprite>): void {
    for (const w of this.waters) w.sync(tiles);
    for (const w of this.sways) w.sync(tiles);
  }

  /**
   * Every piece's state, for the browser tests: whether it is running, and
   * a string of how it looks now (positions, scales, pictures, mesh
   * points), so a piece that moves gives different strings over time. The
   * trees are listed too, by the wind's phase.
   */
  pieces(): PieceState[] {
    const out: PieceState[] = this.runners.map((r, i) => ({
      id: this.named[i]!.id,
      kind: this.named[i]!.kind,
      slot: r.slot ?? null,
      shown: r.root.visible,
      dark: !r.root.renderable || r.root.alpha <= 0.001,
      pose: poseOf(r.root),
    }));
    for (const w of this.sways) {
      out.push({
        id: w.spec.id,
        kind: 'sway',
        slot: null,
        shown: w.root.visible,
        dark: !w.isOn(),
        pose: w.phase(),
      });
    }
    return out;
  }

  stats(): AmbientStats {
    return {
      running: this.running,
      total: this.runners.length + this.waters.length + this.sways.length,
      paused: this.paused,
      still: this.still,
      clock: this.clock,
      updateMs: this.updateMs,
      sea: this.waters.length === 0 ? 'none' : this.seaMode,
      sway: this.sways.length === 0 ? 'none' : this.sways.some((w) => w.isOn()) ? 'on' : 'off',
      fellBack: this.fellBack,
      frameMs: this.watch.median,
    };
  }

  destroy(): void {
    for (const r of this.runners) r.destroy();
    for (const w of this.waters) w.destroy();
    for (const w of this.sways) w.destroy();
    this.runners.length = 0;
    this.waters.length = 0;
    this.sways.length = 0;
    this.glow.destroy(true);
    this.drop.destroy(true);
    this.stroke.destroy(true);
    this.star.destroy(true);
    this.settled.clear();
    this.layer.removeChildren();
  }
}

// ---- the sea ----

/** Master pixels per side of a mask's coarse map of where it's white. */
const SEA_CELL = 128;
/** Master pixels between wave highlights (one per cell of this size). */
const HIGHLIGHT_CELL = 96;
/** The most highlights drawn at once (the whole realm on a big screen). */
const MAX_HIGHLIGHTS = 260;

/**
 * Where a mask (the sea, the trees) is white, read once on the CPU: its
 * value at a point, a coarse map of the squares holding any of it, and so
 * the part of a view, or of a tile, that has any.
 */
class MaskGrid {
  /** The mask's pixels (one byte each) and size. */
  private readonly px: Uint8Array | null;
  private readonly pw: number;
  private readonly ph: number;
  /** Which SEA_CELL squares hold any. */
  private readonly cols: number;
  private readonly rows: number;
  private readonly cells: Uint8Array;

  constructor(
    texture: Texture,
    private readonly rect: Rect,
  ) {
    const read = readMask(texture);
    this.px = read?.px ?? null;
    this.pw = read?.w ?? 1;
    this.ph = read?.h ?? 1;
    this.cols = Math.ceil(rect.w / SEA_CELL);
    this.rows = Math.ceil(rect.h / SEA_CELL);
    this.cells = new Uint8Array(this.cols * this.rows);
    for (let j = 0; j < this.rows; j++) {
      for (let i = 0; i < this.cols; i++) {
        this.cells[j * this.cols + i] = this.px ? (this.anyIn(i, j) ? 1 : 0) : 1;
      }
    }
  }

  /** The mask's value (0 to 255) at a master point. */
  at(mx: number, my: number): number {
    if (!this.px) return 0;
    const r = this.rect;
    const x = Math.floor(((mx - r.x) / r.w) * this.pw);
    const y = Math.floor(((my - r.y) / r.h) * this.ph);
    if (x < 0 || y < 0 || x >= this.pw || y >= this.ph) return 0;
    return this.px[y * this.pw + x]!;
  }

  private anyIn(i: number, j: number): boolean {
    const r = this.rect;
    const x0 = Math.floor(((i * SEA_CELL) / r.w) * this.pw);
    const y0 = Math.floor(((j * SEA_CELL) / r.h) * this.ph);
    const x1 = Math.min(this.pw, Math.ceil((((i + 1) * SEA_CELL) / r.w) * this.pw));
    const y1 = Math.min(this.ph, Math.ceil((((j + 1) * SEA_CELL) / r.h) * this.ph));
    for (let y = y0; y < y1; y++) {
      for (let x = x0; x < x1; x++) if (this.px![y * this.pw + x]! > 12) return true;
    }
    return false;
  }

  /** The bounds of the mask's white inside `view` (master pixels), or null. */
  inView(view: Rect): Rect | null {
    const r = this.rect;
    const i0 = Math.max(0, Math.floor((view.x - r.x) / SEA_CELL));
    const j0 = Math.max(0, Math.floor((view.y - r.y) / SEA_CELL));
    const i1 = Math.min(this.cols - 1, Math.floor((view.x + view.w - r.x) / SEA_CELL));
    const j1 = Math.min(this.rows - 1, Math.floor((view.y + view.h - r.y) / SEA_CELL));
    let a = Infinity;
    let b = Infinity;
    let c = -Infinity;
    let d = -Infinity;
    for (let j = j0; j <= j1; j++) {
      for (let i = i0; i <= i1; i++) {
        if (!this.cells[j * this.cols + i]) continue;
        a = Math.min(a, i);
        b = Math.min(b, j);
        c = Math.max(c, i);
        d = Math.max(d, j);
      }
    }
    if (a === Infinity) return null;
    const x0 = Math.max(view.x, r.x + a * SEA_CELL);
    const y0 = Math.max(view.y, r.y + b * SEA_CELL);
    const x1 = Math.min(view.x + view.w, r.x + (c + 1) * SEA_CELL);
    const y1 = Math.min(view.y + view.h, r.y + (d + 1) * SEA_CELL);
    return x1 > x0 && y1 > y0 ? { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } : null;
  }

  /** True when a tile's square holds any white (only those are copied). */
  covers(s: Sprite): boolean {
    const r = this.rect;
    const i0 = Math.max(0, Math.floor((s.x - r.x) / SEA_CELL));
    const j0 = Math.max(0, Math.floor((s.y - r.y) / SEA_CELL));
    const i1 = Math.min(this.cols - 1, Math.floor((s.x + s.width - r.x) / SEA_CELL));
    const j1 = Math.min(this.rows - 1, Math.floor((s.y + s.height - r.y) / SEA_CELL));
    for (let j = j0; j <= j1; j++) {
      for (let i = i0; i <= i1; i++) if (this.cells[j * this.cols + i]) return true;
    }
    return false;
  }
}

/** Keeps a copy of the map tiles that a mask covers, in step with the
 *  scene's, for a filter to move. */
function syncCopies(
  into: Container,
  copies: Map<string, Sprite>,
  tiles: ReadonlyMap<string, Sprite>,
  grid: MaskGrid,
): void {
  for (const [key, s] of tiles) {
    if (copies.has(key) || !grid.covers(s)) continue;
    const c = new Sprite(s.texture);
    c.position.copyFrom(s.position);
    c.width = s.width;
    c.height = s.height;
    c.zIndex = s.zIndex;
    into.addChild(c);
    copies.set(key, c);
  }
  for (const [key, c] of copies) {
    if (tiles.has(key)) continue;
    c.destroy();
    copies.delete(key);
  }
}

/**
 * The sea all round the island. Rippling: a copy of the map tiles over the
 * sea, shifted by the ripple filter where the sea mask is white (and drawn
 * exactly as the tiles under it everywhere else), run only over the part
 * of the sea on screen. On slow devices: drifting wave highlights instead,
 * small white strokes that come and go on the sea, with no extra pass.
 */
class WaterRunner {
  readonly root = new Container();
  private readonly highlights = new Container();
  private readonly copies = new Map<string, Sprite>();
  private readonly filter: SeaRippleFilter;
  private readonly noise: Sprite;
  private readonly mask: Sprite;
  private readonly area = new Rectangle();
  private readonly grid: MaskGrid;
  private readonly pool: Sprite[] = [];

  constructor(
    layer: Container,
    under: Container,
    readonly spec: WaterSpec,
    maskTexture: Texture,
    private readonly stroke: Texture,
    private mode: 'ripple' | 'highlights',
  ) {
    this.root.label = `water:${spec.id}`;
    this.root.sortableChildren = true;
    this.noise = new Sprite(rippleTexture());
    this.noise.position.set(spec.rect.x, spec.rect.y);
    this.noise.width = spec.size;
    this.noise.height = spec.size;
    this.mask = new Sprite(maskTexture);
    this.mask.position.set(spec.rect.x, spec.rect.y);
    this.mask.width = spec.rect.w;
    this.mask.height = spec.rect.h;
    this.filter = new SeaRippleFilter(this.noise, this.mask);
    if (spec.detail !== undefined && spec.detail < 1) {
      this.filter.resolution = Math.max(0.5, (window.devicePixelRatio || 1) * spec.detail);
    }
    this.root.filters = [this.filter];
    // The filter runs over the part of the sea on screen only. Never let
    // Pixi skip it (it skips a filter whose area falls off screen, and
    // would then draw the tile copies unfiltered, coarse tiles and all):
    // the area is always the sea in view, so nothing is wasted.
    this.filter.clipToViewport = false;
    this.root.filterArea = this.area;
    this.grid = new MaskGrid(maskTexture, spec.rect);
    // The water goes under every mover, the highlights just over it; the
    // two reference sprites are on the stage for their transforms only.
    under.addChildAt(this.root, 0);
    layer.addChildAt(this.highlights, under === layer ? 1 : 0);
    layer.addChild(this.noise, this.mask);
    this.setMode(mode);
  }

  setMode(mode: 'ripple' | 'highlights'): void {
    this.mode = mode;
    // Hidden until update() knows where the sea is on screen: with no
    // filter area yet, Pixi would skip the filter and draw the copies bare.
    this.root.visible = false;
    this.highlights.visible = false;
    if (mode === 'highlights') {
      // The tile copies are only for the ripple.
      for (const c of this.copies.values()) c.destroy();
      this.copies.clear();
    }
  }

  sync(tiles: ReadonlyMap<string, Sprite>): void {
    if (this.mode !== 'ripple') return;
    syncCopies(this.root, this.copies, tiles, this.grid);
  }

  /** Move the sea on screen; false when there's none on screen. */
  update(t: number, zoom: number, view: Rect): boolean {
    const sea = this.grid.inView(view);
    this.root.visible = !!sea && this.mode === 'ripple';
    this.highlights.visible = !!sea && this.mode === 'highlights';
    if (!sea) return false;
    if (this.mode === 'highlights') {
      this.drawHighlights(t, sea);
      return true;
    }
    const s = this.spec;
    // The filter's area is in the layer's own coordinates (master pixels):
    // Pixi takes it to the screen. Padded a few screen pixels for the shift.
    const pad = 8 / Math.max(zoom, 1e-3);
    this.area.x = sea.x - pad;
    this.area.y = sea.y - pad;
    this.area.width = sea.w + 2 * pad;
    this.area.height = sea.h + 2 * pad;
    this.filter.strength = s.strength * zoom;
    const d = t * s.speed;
    this.filter.setDrift(d, d * 0.55, -d * 0.8, d * 0.35);
    this.filter.glint = s.glint;
    return true;
  }

  /**
   * One highlight per HIGHLIGHT_CELL square of sea: each comes and goes on
   * its own cycle at a fresh spot in its square, drifting a little with
   * the wind. Fixed to the painting, so panning and zooming don't move
   * them; past MAX_HIGHLIGHTS squares on screen, every other one shows.
   */
  private drawHighlights(t: number, sea: Rect): void {
    const i0 = Math.floor(sea.x / HIGHLIGHT_CELL);
    const j0 = Math.floor(sea.y / HIGHLIGHT_CELL);
    const i1 = Math.floor((sea.x + sea.w) / HIGHLIGHT_CELL);
    const j1 = Math.floor((sea.y + sea.h) / HIGHLIGHT_CELL);
    const squares = (i1 - i0 + 1) * (j1 - j0 + 1);
    const step = squares > MAX_HIGHLIGHTS * 2.2 ? 2 : 1;
    let n = 0;
    for (let j = j0; j <= j1; j++) {
      for (let i = i0; i <= i1; i++) {
        if (step === 2 && (i + j) % 2) continue;
        if (n >= MAX_HIGHLIGHTS) break;
        const id = j * 64 + i;
        const life = 3.6 + 2.4 * hash01(id * 5 + 1);
        const u = t / life + hash01(id * 5 + 2);
        const cycle = Math.floor(u);
        const k = u - cycle;
        const h = hash01(id * 977 + cycle * 7919);
        const h2 = hash01(id * 613 + cycle * 4099);
        const x = (i + 0.15 + 0.7 * h) * HIGHLIGHT_CELL + 10 * k;
        const y = (j + 0.15 + 0.7 * h2) * HIGHLIGHT_CELL + 2 * k;
        if (this.grid.at(x, y) < 170) continue;
        const sp = this.pool[n] ?? this.addStroke();
        n++;
        sp.visible = true;
        sp.position.set(x, y);
        const size = (20 + 14 * h) * (0.85 + 0.3 * k);
        sp.scale.set(size / this.stroke.width, (size * 0.42) / this.stroke.height);
        sp.rotation = (h2 - 0.5) * 0.5;
        sp.alpha = 0.75 * Math.sin(Math.PI * k) ** 2;
      }
    }
    for (let q = n; q < this.pool.length; q++) this.pool[q]!.visible = false;
  }

  private addStroke(): Sprite {
    const sp = new Sprite(this.stroke);
    sp.anchor.set(0.5);
    this.highlights.addChild(sp);
    this.pool.push(sp);
    return sp;
  }

  destroy(): void {
    this.root.destroy({ children: true });
    this.highlights.destroy({ children: true });
    this.noise.texture.destroy(true);
    this.noise.destroy();
    this.mask.destroy();
  }
}

// ---- the trees ----

/**
 * Trees, bushes and flowers swaying in the wind (logic/realm/ambient
 * `swayLevel`): as the sea's ripple, a copy of the map tiles where the mask
 * is white, moved by {@link SwayFilter}, run only over the trees on screen.
 * It's drawn at the screen's full resolution (the leaves are fine detail),
 * so on a slow device it goes altogether, with the sea's ripple.
 */
class SwayRunner {
  readonly root = new Container();
  private readonly copies = new Map<string, Sprite>();
  private readonly filter: SwayFilter;
  private readonly mask: Sprite;
  private readonly area = new Rectangle();
  private readonly grid: MaskGrid;
  private on = true;
  private last = 0;

  constructor(
    layer: Container,
    under: Container,
    readonly spec: SwayElement,
    maskTexture: Texture,
  ) {
    this.root.label = `sway:${spec.id}`;
    this.root.sortableChildren = true;
    this.mask = new Sprite(maskTexture);
    this.mask.position.set(spec.rect.x, spec.rect.y);
    this.mask.width = spec.rect.w;
    this.mask.height = spec.rect.h;
    this.filter = new SwayFilter(this.mask);
    this.filter.rect = spec.rect;
    const wind = windOf(spec);
    this.filter.setWind(wind.x, wind.y);
    this.filter.resolution = Math.max(0.5, (window.devicePixelRatio || 1) * (spec.detail ?? 1));
    // As the ripple: never let Pixi skip the filter and draw the copies
    // bare; its area is always the trees in view.
    this.filter.clipToViewport = false;
    this.root.filters = [this.filter];
    this.root.filterArea = this.area;
    this.root.visible = false;
    this.grid = new MaskGrid(maskTexture, spec.rect);
    // Under every mover, with the water; the mask is on the stage for its
    // transform only.
    under.addChildAt(this.root, 0);
    layer.addChild(this.mask);
  }

  /** On, or off for good (a slow device). */
  setOn(on: boolean): void {
    this.on = on;
    this.root.visible = false;
    if (!on) {
      for (const c of this.copies.values()) c.destroy();
      this.copies.clear();
    }
  }

  isOn(): boolean {
    return this.on;
  }

  sync(tiles: ReadonlyMap<string, Sprite>): void {
    if (this.on) syncCopies(this.root, this.copies, tiles, this.grid);
  }

  /** The wind's phase now (what the filter was last given), for tests. */
  phase(): string {
    return this.on ? `${this.last.toFixed(3)}` : 'off';
  }

  /** Sway the trees on screen; false when none are (or it's off). */
  update(t: number, zoom: number, view: Rect): boolean {
    const trees = this.on ? this.grid.inView(view) : null;
    this.root.visible = !!trees;
    if (!trees) return false;
    const s = this.spec;
    const pad = s.amount + 2;
    this.area.x = trees.x - pad;
    this.area.y = trees.y - pad;
    this.area.width = trees.w + 2 * pad;
    this.area.height = trees.h + 2 * pad;
    const repeat = SWAY_REPEAT * Math.max(1e-3, s.period);
    this.last = t % repeat;
    this.filter.setSway(this.last, s.period, s.wavelength, s.amount);
    this.filter.pixelsPerMaster = zoom;
    return true;
  }

  destroy(): void {
    this.root.destroy({ children: true });
    this.mask.destroy();
  }
}

/** The sea mask's red channel, read back once on the CPU (it's small), or
 *  null when the image can't be read. */
function readMask(texture: Texture): { px: Uint8Array; w: number; h: number } | null {
  try {
    const src = texture.source.resource as CanvasImageSource | undefined;
    const w = texture.source.pixelWidth;
    const h = texture.source.pixelHeight;
    if (!src || !w || !h) return null;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(src, 0, 0, w, h);
    const data = ctx.getImageData(0, 0, w, h).data;
    const px = new Uint8Array(w * h);
    for (let i = 0; i < px.length; i++) px[i] = data[i * 4]!;
    return { px, w, h };
  } catch {
    return null;
  }
}

// ---- boats ----

/** A painted part bent on a grid, and how it moves. */
interface BentPart {
  mesh: MeshPlane;
  rest: Float32Array;
  motion: NonNullable<Part['motion']>;
  w: number;
  h: number;
  cols: number;
  rows: number;
}

/** Builds a mover's parts: plain sprites, or bent grids for the parts that
 *  billow or flutter. */
function buildParts(
  parts: readonly Part[],
  frame: (name: string) => Texture,
  into: Container,
): BentPart[] {
  const bent: BentPart[] = [];
  for (const p of parts) {
    const tex = frame(p.frame);
    if (!p.motion) {
      const s = new Sprite(tex);
      s.position.set(p.x, p.y);
      into.addChild(s);
      continue;
    }
    const cols = 8;
    const rows = 8;
    const mesh = new MeshPlane({ texture: tex, verticesX: cols, verticesY: rows });
    mesh.position.set(p.x, p.y);
    into.addChild(mesh);
    const data = mesh.geometry.getBuffer('aPosition').data as Float32Array;
    bent.push({
      mesh,
      rest: data.slice(),
      motion: p.motion,
      w: tex.width,
      h: tex.height,
      cols,
      rows,
    });
  }
  return bent;
}

/** Move a bent part's grid for time `t`. */
function bend(b: BentPart, t: number): void {
  const buffer = b.mesh.geometry.getBuffer('aPosition');
  const out = buffer.data as Float32Array;
  const { amount, period, hold } = b.motion;
  const phase = b.motion.phase ?? 0;
  const fill = 0.55 + 0.45 * wave(t, period, phase);
  for (let j = 0; j < b.rows; j++) {
    for (let i = 0; i < b.cols; i++) {
      const k = (j * b.cols + i) * 2;
      const u = i / (b.cols - 1);
      const v = j / (b.rows - 1);
      let dx = 0;
      let dy = 0;
      if (b.motion.kind === 'billow') {
        // Held along the top (the yard): the cloth fills out sideways,
        // most at the foot, with a small ripple running down it.
        const belly = Math.sin(Math.PI * u) * v;
        dx =
          amount * fill * belly +
          amount * 0.22 * v * Math.sin(2 * Math.PI * (t / (period * 0.4) + v * 0.8 + u * 0.5));
        dy = -amount * 0.3 * fill * belly;
      } else {
        // Held along one side (the mast): a wave runs out to the free end.
        const d = hold === 'right' ? 1 - u : u;
        const w = Math.sin(2 * Math.PI * (d * 1.2 - t / period + phase));
        dy = amount * d * w * (0.75 + 0.25 * v);
        dx =
          (hold === 'right' ? 1 : -1) *
          amount *
          0.25 *
          d *
          (1 - Math.cos(2 * Math.PI * (d * 1.2 - t / period + phase)));
      }
      out[k] = b.rest[k]! + dx;
      out[k + 1] = b.rest[k + 1]! + dy;
    }
  }
  buffer.update();
}

/** Foam and a soft shade under a hull, to sit it in the water. */
function buildWaterline(
  w: Waterline | undefined,
  frame: (name: string) => Texture,
  glow: Texture,
  into: Container,
): Sprite | null {
  if (!w) return null;
  const shade = new Sprite(glow);
  shade.anchor.set(0.5);
  shade.tint = 0x0d3550;
  shade.alpha = 0.35;
  shade.width = w.w * 1.05;
  shade.height = w.h * 1.1;
  shade.position.set(0, w.dy + w.h * 0.12);
  into.addChild(shade);
  if (!w.ring) return null;
  const ring = new Sprite(frame(w.ring));
  ring.anchor.set(0.5);
  ring.width = w.w;
  ring.height = w.h;
  ring.alpha = w.alpha;
  ring.position.set(0, w.dy);
  into.addChild(ring);
  return ring;
}

/** A ship moored and rocking, or a boat sailing to and fro. */
class BoatRunner implements Runner {
  readonly root = new Container();
  readonly area: Rect;
  readonly slot?: number;
  readonly minZoom?: number;
  private readonly hull = new Container();
  private readonly bent: BentPart[];
  private readonly ring: Sprite | null;
  private readonly ringW: number;
  private readonly outDir: number;

  constructor(
    private readonly e: RigElement | PathElement,
    frame: (name: string) => Texture,
    glow: Texture,
    private readonly still = false,
  ) {
    this.root.label = `ambient:${e.id}`;
    this.area = e.area;
    this.slot = e.slot;
    this.minZoom = e.minZoom;
    this.ring = buildWaterline(e.waterline, frame, glow, this.root);
    this.ringW = e.waterline?.w ?? 0;
    this.root.addChild(this.hull);
    this.bent = buildParts(e.parts, frame, this.hull);
    if (e.kind === 'rig') {
      this.root.position.set(e.at[0], e.at[1]);
      this.outDir = 1;
    } else {
      const first = e.path[0] ?? [0, 0];
      const last = e.path[e.path.length - 1] ?? first;
      this.outDir = last[0] >= first[0] ? 1 : -1;
    }
  }

  /** Grey and dim with its building while locked, colour coming back as it opens. */
  setLocked(level: number): void {
    greyLocked(this.root, level);
  }

  update(t: number): void {
    const e = this.e;
    let moving = false;
    if (e.kind === 'path') {
      // Reduced motion: at rest where the painting has it, the route's start.
      const at = followPath(e.path, e.speed, e.pause, this.still ? 0 : t);
      moving = at.moving && !this.still;
      this.root.position.set(at.x, at.y);
      // Turning round: the art faces `faces`; it turns to face its way.
      if (e.turns !== false) this.root.scale.x = at.facing * this.outDir * e.faces;
    }
    this.hull.y = e.bob ? e.bob.px * wave(t, e.bob.period) : 0;
    if (moving && e.kind === 'path' && e.rattle) {
      // A cart's jolts over the sleepers.
      this.hull.y += e.rattle * Math.abs(Math.sin(t * 17)) * Math.sin(t * 5.3);
    }
    this.hull.rotation = e.rock ? ((e.rock.deg * Math.PI) / 180) * wave(t, e.rock.period, 0.25) : 0;
    if (this.ring) {
      const breathe = 1 + 0.04 * wave(t, 2.6);
      this.ring.width = this.ringW * breathe;
    }
    for (const b of this.bent) bend(b, t);
  }

  /** Back where the painting has it. */
  settle(): void {
    this.update(0);
  }

  destroy(): void {
    this.root.destroy({ children: true });
  }
}

// ---- birds ----

class FlockRunner implements Runner {
  readonly root = new Container();
  readonly area: Rect;
  readonly slot?: number;
  private readonly birds: { bird: Sprite; shadow: Sprite | null }[] = [];
  private readonly frames: Texture[];

  constructor(
    private readonly e: FlockElement,
    frame: (name: string) => Texture,
    private readonly glow: Texture,
  ) {
    this.root.label = `ambient:${e.id}`;
    this.area = e.area;
    this.slot = e.slot;
    this.frames = e.frames.map(frame);
  }

  private pooled(i: number): { bird: Sprite; shadow: Sprite | null } {
    let b = this.birds[i];
    if (b) return b;
    let shadow: Sprite | null = null;
    if (this.e.shadow) {
      shadow = new Sprite(this.glow);
      shadow.anchor.set(0.5);
      shadow.tint = 0x0d3550;
      this.root.addChild(shadow);
    }
    const bird = new Sprite(this.frames[0]);
    this.root.addChild(bird);
    b = { bird, shadow };
    this.birds[i] = b;
    return b;
  }

  /** Grey and dim with its building while locked, colour coming back as it opens. */
  setLocked(level: number): void {
    greyLocked(this.root, level);
  }

  update(t: number): void {
    const flying = birdsAt(this.e, t);
    flying.forEach((f, i) => {
      const { bird, shadow } = this.pooled(i);
      const tex = this.frames[f.frame] ?? this.frames[0]!;
      const pivot = this.e.pivots[f.frame] ?? [tex.width / 2, tex.height / 2];
      bird.texture = tex;
      bird.anchor.set(pivot[0] / tex.width, pivot[1] / tex.height);
      bird.position.set(f.x, f.y);
      bird.scale.set(f.dir * f.scale, f.scale);
      bird.visible = true;
      if (shadow && this.e.shadow) {
        shadow.visible = true;
        shadow.position.set(f.x + 10, f.y + this.e.shadow.dy);
        shadow.width = 46 * f.scale;
        shadow.height = 16 * f.scale;
        shadow.alpha = this.e.shadow.alpha;
      }
    });
    for (let i = flying.length; i < this.birds.length; i++) {
      const b = this.birds[i]!;
      b.bird.visible = false;
      if (b.shadow) b.shadow.visible = false;
    }
  }

  destroy(): void {
    this.root.destroy({ children: true });
  }
}

// ---- the sea serpent ----

const BODY_POINTS = 28;
const RINGS = 8;

class SerpentRunner implements Runner {
  readonly root = new Container();
  readonly area: Rect;
  readonly slot?: number;
  private readonly clip = new Container();
  private readonly points: Point[];
  private readonly body: MeshRope;
  private readonly head: Sprite;
  private readonly rings: Sprite[] = [];
  private readonly neckRing: Sprite;
  private readonly splash: Sprite;
  private readonly splashRing: Sprite;

  constructor(
    private readonly e: SerpentElement,
    frame: (name: string) => Texture,
  ) {
    this.root.label = `ambient:${e.id}`;
    this.area = e.area;
    this.slot = e.slot;
    const ring = frame(e.ring);
    // The body and head, cut off at the waterline.
    this.points = Array.from({ length: BODY_POINTS }, () => new Point());
    this.body = new MeshRope({ texture: frame(e.body), points: this.points, textureScale: 0 });
    this.head = new Sprite(frame(e.head));
    const headTex = this.head.texture;
    this.head.anchor.set(e.neck[0] / headTex.width, e.neck[1] / headTex.height);
    this.clip.addChild(this.body, this.head);
    const water = new Graphics()
      .rect(-e.length - 200, -600, e.length + 600, 600 + e.thickness * 0.15)
      .fill(0xffffff);
    this.clip.mask = water;
    this.root.addChild(water, this.clip);
    // The foam where it breaks the surface (the body shows through the
    // middle of each ring), and round the neck.
    for (let i = 0; i < RINGS; i++) {
      const r = new Sprite(ring);
      r.anchor.set(0.5);
      r.visible = false;
      this.root.addChild(r);
      this.rings.push(r);
    }
    this.neckRing = new Sprite(ring);
    this.neckRing.anchor.set(0.5);
    this.root.addChild(this.neckRing);
    // Over everything: the splash of breaking the surface.
    this.splashRing = new Sprite(ring);
    this.splashRing.anchor.set(0.5);
    this.splash = new Sprite(frame(e.splash));
    this.splash.anchor.set(0.5, 0.8);
    this.root.addChild(this.splashRing, this.splash);
  }

  /** Grey and dim with its building while locked, colour coming back as it opens. */
  setLocked(level: number): void {
    greyLocked(this.root, level);
  }

  update(t: number): void {
    const e = this.e;
    const st = serpentAt(e, t);
    const spot = e.spots[st.spot] ?? e.spots[0];
    if (!spot) return;
    const drift = e.swim * Math.min(st.since, e.cycle.rise + e.cycle.swim + e.cycle.dive);
    this.root.position.set(spot.at[0] + spot.faces * drift, spot.at[1]);
    this.root.scale.x = spot.faces;
    const up = st.up;
    this.clip.visible = up > 0.001;
    // Body: tail first, so the rope (and its texture) runs towards the head.
    const pts = serpentBody(e, t, up, BODY_POINTS);
    const sink = e.thickness * 0.35;
    for (let i = 0; i < BODY_POINTS; i++) {
      const p = pts[BODY_POINTS - 1 - i]!;
      this.points[i]!.set(p.x, -p.h + sink + (1 - up) * e.thickness * 1.3);
    }
    // Head: rises out of the water and sways a little.
    const headH = this.head.texture.height;
    this.head.position.set(0, (1 - up) * headH * 1.05);
    this.head.rotation = 0.06 * wave(t, 3.4) * up;
    // Foam where the body breaks the surface, and round the neck.
    const crossings = up > 0.05 ? surfaceCrossings(e, t) : [];
    this.rings.forEach((r, i) => {
      const d = crossings[i];
      r.visible = d !== undefined;
      if (d === undefined) return;
      r.position.set(-d, 0);
      r.width = e.thickness * 1.9;
      r.height = e.thickness * 0.62;
      r.alpha = 0.75 * up;
    });
    this.neckRing.visible = up > 0.05;
    this.neckRing.position.set(e.thickness * 0.1, 2);
    this.neckRing.width = e.thickness * 2.4 * (1 + 0.05 * wave(t, 1.9));
    this.neckRing.height = e.thickness * 0.8;
    this.neckRing.alpha = 0.85 * up;
    // The splash, as it breaks the surface going down or coming up.
    const k = st.splash / 1.3;
    const on = k >= 0 && k < 1;
    this.splash.visible = on;
    this.splashRing.visible = on;
    if (on) {
      const ease = 1 - (1 - k) ** 2;
      this.splash.position.set(0, 4);
      this.splash.scale.set(0.55 + 0.6 * ease);
      this.splash.alpha = 1 - k ** 1.6;
      this.splashRing.position.set(0, 4);
      this.splashRing.width = e.thickness * (1.6 + 3.2 * ease);
      this.splashRing.height = e.thickness * (0.55 + 1.1 * ease);
      this.splashRing.alpha = 0.9 * (1 - k);
    }
  }

  destroy(): void {
    this.root.destroy({ children: true });
  }
}

// ---- the lighthouse ----

class BeamRunner implements Runner {
  readonly root = new Container();
  readonly area: Rect;
  readonly slot?: number;
  private readonly turn = new Container();
  private readonly beam: Sprite;
  private readonly glow: Sprite;

  constructor(
    private readonly e: BeamElement,
    frame: (name: string) => Texture,
    glowTexture: Texture,
    still: boolean,
  ) {
    this.root.label = `ambient:${e.id}`;
    this.area = e.area;
    this.slot = e.slot;
    this.root.position.set(e.at[0], e.at[1]);
    // The beam turns in a squashed circle: the map is seen at a slant.
    this.turn.scale.y = e.tilt;
    this.beam = new Sprite(frame(e.frame));
    this.beam.anchor.set(0.02, 0.5);
    this.beam.width = e.length;
    this.beam.height = e.width;
    this.beam.blendMode = 'add';
    this.beam.visible = !still;
    this.turn.addChild(this.beam);
    this.glow = new Sprite(glowTexture);
    this.glow.anchor.set(0.5);
    this.glow.tint = 0xffe6a0;
    this.glow.blendMode = 'add';
    this.glow.width = e.glow.radius * 2;
    this.glow.height = e.glow.radius * 2;
    this.glow.alpha = e.glow.alpha;
    this.root.addChild(this.turn, this.glow);
  }

  /** Gone while its building is locked, back as it opens. */
  setLocked(level: number): void {
    fadeLocked(this.root, level);
  }

  update(t: number): void {
    const { angle, light } = beamAt(this.e, t);
    this.beam.rotation = angle;
    this.beam.alpha = this.e.alpha * light;
    // The lamp brightens as the beam swings past the viewer.
    this.glow.alpha = this.e.glow.alpha * (0.65 + 0.35 * light);
  }

  destroy(): void {
    this.root.destroy({ children: true });
  }
}

/** A soft round dot, white in the middle fading to clear: glows, shadows. */
export function softDot(size = 64): Texture {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.45, 'rgba(255,255,255,0.55)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  return Texture.from(canvas);
}

// ---- clouds ----

/** A cloud and its shadow. The shadow is the same painting, darkened and
 *  faint, drawn under every cloud. */
class CloudRunner implements Runner {
  readonly root: Sprite;
  readonly shadow: Sprite | null;
  readonly area: Rect;

  constructor(
    private readonly e: CloudElement,
    frame: (name: string) => Texture,
  ) {
    const tex = frame(e.frame);
    this.root = new Sprite(tex);
    this.root.label = `ambient:${e.id}`;
    this.root.anchor.set(0.5);
    this.root.scale.set(e.scale * (e.flip ? -1 : 1), e.scale);
    this.root.renderable = !e.shadowOnly;
    this.shadow = null;
    const sh = e.shadow;
    if (sh) {
      this.shadow = new Sprite(tex);
      this.shadow.anchor.set(0.5);
      this.shadow.tint = 0x0d3550;
      const k = e.scale * (sh.scale ?? 1);
      this.shadow.scale.set(k * (e.flip ? -1 : 1), k);
    }
    // Everywhere it and its shadow can reach.
    const w = (tex.width * e.scale) / 2;
    const h = (tex.height * e.scale) / 2;
    const [ax, ay] = e.at;
    const [bx, by] = e.cross?.to ?? e.at;
    const dx = e.drift ? Math.abs(e.drift.dx) : 0;
    const dy = e.drift ? Math.abs(e.drift.dy) : 0;
    const x0 = Math.min(ax, bx) - dx - w;
    const y0 = Math.min(ay, by) - dy - h;
    const x1 = Math.max(ax, bx) + dx + w + Math.max(0, sh?.dx ?? 0);
    const y1 = Math.max(ay, by) + dy + h + Math.max(0, sh?.dy ?? 0);
    this.area = { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
  }

  update(t: number): void {
    const c = cloudAt(this.e, t);
    this.root.position.set(c.x, c.y);
    this.root.alpha = c.alpha;
    const sh = this.e.shadow;
    if (this.shadow && sh) {
      this.shadow.position.set(c.x + sh.dx, c.y + sh.dy);
      this.shadow.alpha = sh.alpha * (c.alpha / (this.e.alpha ?? 1));
    }
  }

  destroy(): void {
    this.root.destroy();
    this.shadow?.destroy();
  }
}

// ---- smoke and steam ----

/** How far ahead the clock runs for smoke and spray, so a chimney is
 *  already smoking when the map opens. */
const WARM = 30;

class PlumeRunner implements Runner {
  readonly root = new Container();
  readonly area: Rect;
  readonly slot?: number;
  private readonly frames: Texture[];
  private readonly pool: Sprite[] = [];
  private readonly tint: number;

  constructor(
    private readonly e: PlumeElement,
    frame: (name: string) => Texture,
  ) {
    this.root.label = `ambient:${e.id}`;
    this.area = e.area;
    this.slot = e.slot;
    this.slot = e.slot;
    this.frames = e.frames.map(frame);
    this.tint = e.tint ? parseInt(e.tint.replace('#', ''), 16) : 0xffffff;
  }

  /** Gone while its building is locked (the dragon's breath). */
  setLocked(level: number): void {
    fadeLocked(this.root, level);
  }

  update(t: number): void {
    const puffs = puffsAt(this.e, t + WARM);
    puffs.forEach((p, i) => {
      let s = this.pool[i];
      if (!s) {
        s = new Sprite(this.frames[0]);
        s.anchor.set(0.5);
        s.tint = this.tint;
        this.root.addChild(s);
        this.pool.push(s);
      }
      const tex = this.frames[p.frame] ?? this.frames[0]!;
      s.texture = tex;
      s.visible = true;
      s.position.set(p.x, p.y);
      s.scale.set(p.size / Math.max(1, tex.width));
      s.rotation = p.rotation;
      s.alpha = p.alpha;
    });
    for (let i = puffs.length; i < this.pool.length; i++) this.pool[i]!.visible = false;
  }

  destroy(): void {
    this.root.destroy({ children: true });
  }
}

// ---- fountains and the geyser ----

class SprayRunner implements Runner {
  readonly root = new Container();
  readonly area: Rect;
  readonly slot?: number;
  private readonly pool: Sprite[] = [];
  private readonly tint: number;

  constructor(
    private readonly e: SprayElement,
    private readonly drop: Texture,
  ) {
    this.root.label = `ambient:${e.id}`;
    this.area = e.area;
    this.slot = e.slot;
    this.tint = e.tint ? parseInt(e.tint.replace('#', ''), 16) : 0xffffff;
  }

  /** Gone while its building is locked, back as it opens. */
  setLocked(level: number): void {
    fadeLocked(this.root, level);
  }

  update(t: number): void {
    const drops = dropsAt(this.e, t + WARM);
    drops.forEach((d, i) => {
      let s = this.pool[i];
      if (!s) {
        s = new Sprite(this.drop);
        s.anchor.set(0.5);
        s.tint = this.tint;
        this.root.addChild(s);
        this.pool.push(s);
      }
      s.visible = true;
      s.position.set(d.x, d.y);
      s.rotation = d.rotation;
      s.scale.set(d.size / this.drop.width, (d.size * 0.42) / this.drop.height);
      s.alpha = d.alpha;
    });
    for (let i = drops.length; i < this.pool.length; i++) this.pool[i]!.visible = false;
  }

  destroy(): void {
    this.root.destroy({ children: true });
  }
}

// ---- walkers ----

/** Columns and rows of a walker's bent grid: enough rows that the legs bend
 *  from where they start, not from the middle of the body. */
const WALK_COLS = 8;
const WALK_ROWS = 14;

/** A walker: its picture on a grid whose legs swing as it walks. */
interface WalkerView {
  holder: Container;
  mesh: MeshPlane;
  rest: Float32Array;
  shadow: Sprite | null;
  scale: number;
  /** The grid is at rest (nothing to bend while standing). */
  still: boolean;
}

/** Walkers in single file: a camel caravan, a knight on his horse. */
class TrainRunner implements Runner {
  readonly root = new Container();
  readonly area: Rect;
  readonly slot?: number;
  private readonly shadows = new Container();
  private readonly bodies = new Container();
  private readonly views: WalkerView[] = [];
  private readonly outDir: number;
  /** Reduced motion: the clock time shown (standing at home). */
  private readonly restAt: number | null;

  constructor(
    private readonly e: TrainElement,
    frame: (name: string) => Texture,
    glow: Texture,
    still: boolean,
  ) {
    this.root.label = `ambient:${e.id}`;
    this.area = e.area;
    this.slot = e.slot;
    this.bodies.sortableChildren = true;
    this.root.addChild(this.shadows, this.bodies);
    const first = e.path[0] ?? [0, 0];
    const last = e.path[e.path.length - 1] ?? first;
    this.outDir = last[0] >= first[0] ? 1 : -1;
    for (const w of e.walkers) {
      const tex = frame(w.frame);
      const holder = new Container();
      const mesh = new MeshPlane({ texture: tex, verticesX: WALK_COLS, verticesY: WALK_ROWS });
      mesh.position.set(-w.feet[0], -w.feet[1]);
      holder.addChild(mesh);
      this.bodies.addChild(holder);
      let shadow: Sprite | null = null;
      if (e.shadow) {
        shadow = new Sprite(glow);
        shadow.anchor.set(0.5);
        shadow.tint = 0x3a2410;
        shadow.width = e.shadow.w;
        shadow.height = e.shadow.h;
        shadow.alpha = e.shadow.alpha;
        this.shadows.addChild(shadow);
      }
      const data = mesh.geometry.getBuffer('aPosition').data as Float32Array;
      this.views.push({
        holder,
        mesh,
        rest: data.slice(),
        shadow,
        scale: w.scale ?? 1,
        still: true,
      });
    }
    // Reduced motion: standing at home, `rest` seconds into the wait there
    // (by default half way, still facing the way they came); on a loop,
    // standing `rest` seconds round it.
    const rest = Math.min(e.wait[0], e.rest ?? e.wait[0] / 2);
    this.restAt = !still
      ? null
      : e.loop
        ? (e.rest ?? 0) - (e.offset ?? 0)
        : trainLap(e).lap - (e.offset ?? 0) - e.wait[0] + rest;
  }

  /** Gone while its building is locked (the horses in the lists). */
  setLocked(level: number): void {
    fadeLocked(this.root, level);
  }

  update(t: number): void {
    const e = this.e;
    const states = walkersAt(e, this.restAt ?? t);
    const g = e.gait;
    // Standing still (a loop never stops of itself).
    if (this.restAt !== null) for (const s of states) s.pace = 0;
    states.forEach((s, i) => {
      const view = this.views[i];
      if (!view) return;
      const k = view.scale;
      // The body rises as the legs pass under it.
      const lift = g.bob * s.pace * Math.abs(Math.cos(2 * Math.PI * s.step));
      view.holder.position.set(s.x, s.y - lift);
      view.holder.zIndex = s.y;
      view.holder.scale.set(k * s.facing * this.outDir * e.faces, k);
      if (view.shadow) view.shadow.position.set(s.x, s.y);
      if (s.pace <= 0.001 && view.still) return;
      this.bendLegs(view, s.step, s.pace);
      view.still = s.pace <= 0.001;
    });
  }

  /** Swing the legs: front against back, most at the feet. */
  private bendLegs(view: WalkerView, step: number, pace: number): void {
    const buffer = view.mesh.geometry.getBuffer('aPosition');
    const out = buffer.data as Float32Array;
    const swing = (this.e.gait.swing / view.scale) * pace;
    for (let j = 0; j < WALK_ROWS; j++) {
      const v = j / (WALK_ROWS - 1);
      for (let i = 0; i < WALK_COLS; i++) {
        const q = (j * WALK_COLS + i) * 2;
        // From the front: the art faces left (-1) or right (1).
        const u = i / (WALK_COLS - 1);
        const front = this.e.faces === -1 ? u : 1 - u;
        out[q] = view.rest[q]! + swing * legSwing(this.e.gait, step, front, v);
        out[q + 1] = view.rest[q + 1]!;
      }
    }
    buffer.update();
  }

  destroy(): void {
    this.root.destroy({ children: true });
  }
}

// ---- rail trains ----

type RailTrain = TrainElement & { rail: RailSpec };

function isRailTrain(e: TrainElement): e is RailTrain {
  return !!e.rail;
}

/**
 * A rail train (a train element with a `rail`): cars drawn over the painting,
 * each turned to put both wheel sets on the painted rail, running one way
 * across the painting and waiting off it. It is never tappable: nothing in
 * the ambient layer takes a tap, and its own root takes none either. Where
 * the rails pass behind a painted building (`rail.hides`) the train is
 * left out by a mask of that building's shape, so the painting under it, and
 * the grey veil of a locked building, show as they are.
 */
class RailRunner implements Runner {
  readonly root = new Container();
  readonly area: Rect;
  readonly minZoom?: number;
  private readonly bodies = new Container();
  private readonly cars: Sprite[] = [];
  /** Hidden between trips: nothing of the train is on the painting. */
  private shown = false;

  constructor(
    private readonly e: RailTrain,
    frame: (name: string) => Texture,
    private readonly still: boolean,
  ) {
    const rail = e.rail;
    this.root.label = `ambient:${e.id}`;
    this.root.eventMode = 'none';
    this.area = e.area;
    this.minZoom = e.minZoom;
    this.root.addChild(this.bodies);
    for (const w of e.walkers) {
      const car = new Sprite(frame(w.frame));
      car.pivot.set(w.feet[0], w.feet[1]);
      car.eventMode = 'none';
      this.cars.push(car);
    }
    // The cars behind drawn first, so each couples under the one in front of it.
    for (const car of [...this.cars].reverse()) this.bodies.addChild(car);
    if (rail.hides.length > 0) {
      const hide = new Graphics();
      hide.label = 'rail-hides';
      for (const h of rail.hides) hide.poly(h.shape.flatMap(([x, y]) => [x, y])).fill(0xffffff);
      this.root.addChild(hide);
      this.bodies.setMask({ mask: hide, inverse: true });
    }
    this.show(false);
  }

  private show(on: boolean): void {
    this.shown = on;
    this.bodies.visible = on;
  }

  update(t: number): void {
    // Reduced motion shows the train as it waits: off the painting.
    const cars = this.still ? [] : railCarsAt(this.e, t);
    if (cars.length === 0) {
      if (this.shown) this.show(false);
      return;
    }
    if (!this.shown) this.show(true);
    cars.forEach((c, i) => {
      const car = this.cars[i];
      if (!car) return;
      car.position.set(c.x, c.y);
      car.rotation = c.rotation;
    });
  }

  settle(): void {
    this.show(false);
  }

  destroy(): void {
    this.root.destroy({ children: true });
  }
}

// ---- leaping ----

/** A leaper, its waterline cut, and the splashes it makes. */
interface LeapView {
  body: Sprite;
  cut: Graphics;
  splashes: { splash: Sprite; ring: Sprite }[];
}

/** Dolphins leaping now and then. */
class LeapRunner implements Runner {
  readonly root = new Container();
  readonly area: Rect;
  readonly slot?: number;
  private readonly views: LeapView[] = [];
  private readonly scale: number;

  constructor(
    private readonly e: LeapElement,
    private readonly frame: (name: string) => Texture,
    private readonly still: boolean,
  ) {
    this.root.label = `ambient:${e.id}`;
    this.area = e.area;
    this.slot = e.slot;
    this.scale = e.scale ?? 1;
  }

  private view(i: number): LeapView {
    let v = this.views[i];
    if (v) return v;
    const e = this.e;
    const body = new Sprite(this.frame(e.frame));
    body.anchor.set(0.5);
    // Only what's above the surface shows.
    const cut = new Graphics().rect(-600, -600, 1200, 600).fill(0xffffff);
    body.mask = cut;
    const splashes = [0, 1].map(() => {
      const ring = new Sprite(this.frame(e.ring));
      ring.anchor.set(0.5);
      const splash = new Sprite(this.frame(e.splash));
      splash.anchor.set(0.5, 0.8);
      return { splash, ring };
    });
    // Rings on the water, then the leaper, then the splashes over it.
    this.root.addChild(...splashes.map((s) => s.ring), cut, body, ...splashes.map((s) => s.splash));
    v = { body, cut, splashes };
    this.views[i] = v;
    return v;
  }

  /** Grey and dim with its building while locked, colour coming back as it opens. */
  setLocked(level: number): void {
    greyLocked(this.root, level);
  }

  update(t: number): void {
    const e = this.e;
    // Reduced motion: one leaper at the top of its leap at the first spot.
    const leaps = this.still ? this.restLeaps() : leapsAt(e, t);
    const size = e.length * 0.36;
    leaps.forEach((l, i) => {
      const v = this.view(i);
      v.body.visible = !!l.body;
      v.cut.visible = !!l.body;
      if (l.body) {
        v.body.position.set(l.body.x, l.body.y);
        v.body.rotation = l.body.rotation;
        v.body.scale.set(this.scale * l.dir * e.faces, this.scale);
        v.cut.position.set(l.body.x, l.water + 1);
      }
      // A splash as it breaks the surface going out, and coming back in.
      const c = leapCrossing(e);
      [
        { at: l.out, from: c * e.air - 0.1 },
        { at: l.in, from: (1 - c) * e.air - 0.1 },
      ].forEach((s, j) => {
        const { splash, ring } = v.splashes[j]!;
        const k = this.still ? -1 : (l.age - s.from) / SPLASH_SECONDS;
        const on = k >= 0 && k < 1;
        splash.visible = on;
        ring.visible = on;
        if (!on) return;
        const ease = 1 - (1 - k) ** 2;
        splash.position.set(s.at[0], s.at[1] + 3);
        splash.scale.set((size / Math.max(1, splash.texture.width)) * (0.6 + 0.6 * ease));
        splash.alpha = 0.95 * (1 - k ** 1.4);
        ring.position.set(s.at[0], s.at[1] + 2);
        ring.width = size * (0.8 + 1.6 * ease);
        ring.height = size * (0.28 + 0.5 * ease);
        ring.alpha = 0.85 * (1 - k);
      });
    });
    for (let i = leaps.length; i < this.views.length; i++) {
      const v = this.views[i]!;
      v.body.visible = false;
      v.cut.visible = false;
      for (const s of v.splashes) {
        s.splash.visible = false;
        s.ring.visible = false;
      }
    }
  }

  /** The leaper held at the top of a leap from the first spot. */
  private restLeaps(): ReturnType<typeof leapsAt> {
    const spot = this.e.spots[0];
    return spot ? [leapAt(this.e, 0, spot.at, spot.dir, this.e.air * 0.5)] : [];
  }

  destroy(): void {
    this.root.destroy({ children: true });
  }
}

// ---- lights ----

/** Soft glows over the painting's lights. */
class LightsRunner implements Runner {
  readonly root = new Container();
  readonly area: Rect;
  readonly slot?: number;
  private readonly glows: Sprite[] = [];

  constructor(
    private readonly e: LightsElement,
    frame: (name: string) => Texture,
    glow: Texture,
    private readonly still: boolean,
  ) {
    this.root.label = `ambient:${e.id}`;
    this.area = e.area;
    this.slot = e.slot;
    const tint = parseInt(e.colour.replace('#', ''), 16);
    for (const [x, y, r] of e.points ?? []) {
      const s = new Sprite(glow);
      s.anchor.set(0.5);
      s.position.set(x, y);
      s.width = r * 2;
      s.height = r * 2;
      s.tint = tint;
      this.add(s);
    }
    // Glow pictures keep their own colours.
    for (const g of e.sprites ?? []) {
      const s = new Sprite(frame(g.frame));
      s.position.set(g.x, g.y);
      s.scale.set(g.scale);
      this.add(s);
    }
  }

  private add(s: Sprite): void {
    s.blendMode = 'add';
    this.root.addChild(s);
    this.glows.push(s);
  }

  update(t: number): void {
    const e = this.e;
    const n = this.glows.length;
    this.glows.forEach((s, i) => {
      const level = this.still ? (1 + e.low) / 2 : lightLevel(e, t, i, n);
      s.alpha = e.alpha * level;
    });
  }

  /** Unlit while its building is locked. */
  setLocked(level: number): void {
    fadeLocked(this.root, level);
  }

  destroy(): void {
    this.root.destroy({ children: true });
  }
}

// ---- rivers ----

/** Strokes of light drifting down a river. */
class FlowRunner implements Runner {
  readonly root = new Container();
  readonly area: Rect;
  readonly slot?: number;
  private readonly pool: Sprite[] = [];
  private readonly tint: number;

  constructor(
    private readonly e: FlowElement,
    private readonly stroke: Texture,
  ) {
    this.root.label = `ambient:${e.id}`;
    this.area = e.area;
    this.slot = e.slot;
    this.tint = e.tint ? parseInt(e.tint.replace('#', ''), 16) : 0xffffff;
  }

  /** Gone while its building is locked, back as it opens. */
  setLocked(level: number): void {
    fadeLocked(this.root, level);
  }

  update(t: number): void {
    const streaks = streaksAt(this.e, t);
    streaks.forEach((k, i) => {
      let s = this.pool[i];
      if (!s) {
        s = new Sprite(this.stroke);
        s.anchor.set(0.5);
        s.tint = this.tint;
        this.root.addChild(s);
        this.pool.push(s);
      }
      s.visible = true;
      s.position.set(k.x, k.y);
      s.rotation = k.rotation;
      s.scale.set(k.size / this.stroke.width, (k.size * 0.38) / this.stroke.height);
      s.alpha = k.alpha;
    });
    for (let i = streaks.length; i < this.pool.length; i++) this.pool[i]!.visible = false;
  }

  destroy(): void {
    this.root.destroy({ children: true });
  }
}

// ---- life in place ----

/** Points across an idle piece's grid: about one every 6 frame pixels, so
 *  a turning head bends from its neck, not from the middle of the body. */
function gridPoints(px: number): number {
  return Math.min(20, Math.max(4, Math.round(px / 6) + 1));
}

/** One picture of an idle piece on its bent grid. */
class IdleMesh {
  readonly mesh: MeshPlane;
  private readonly restPoints: Float32Array;
  private readonly weights: Float32Array[];
  private readonly h: number;
  private atRest = true;

  constructor(
    tex: Texture,
    private readonly motion: Pick<IdleElement, 'joints' | 'breathe'>,
  ) {
    this.mesh = new MeshPlane({
      texture: tex,
      verticesX: gridPoints(tex.width),
      verticesY: gridPoints(tex.height),
    });
    this.restPoints = (this.mesh.geometry.getBuffer('aPosition').data as Float32Array).slice();
    this.weights = (motion.joints ?? []).map((j) => jointWeights(j, this.restPoints));
    this.h = tex.height;
  }

  update(t: number): void {
    const buffer = this.mesh.geometry.getBuffer('aPosition');
    const moving = idlePose(
      this.motion,
      t,
      this.restPoints,
      this.weights,
      this.h,
      buffer.data as Float32Array,
    );
    if (moving || !this.atRest) buffer.update();
    this.atRest = !moving;
  }

  settle(): void {
    const buffer = this.mesh.geometry.getBuffer('aPosition');
    (buffer.data as Float32Array).set(this.restPoints);
    buffer.update();
    this.atRest = true;
  }
}

/** A piece living in place: grazing, swishing, waving, breathing, bobbing,
 *  now and then fading to a second pose (the yeti's wave), or easing
 *  through a cycle of pictures (the dragon's breath). */
class IdleRunner implements Runner {
  readonly root = new Container();
  readonly area: Rect;
  readonly minZoom?: number;
  /** The picture (and its second pose), scaled and mirrored. */
  private readonly body = new Container();
  private readonly main: IdleMesh;
  private readonly pose: IdleMesh | null = null;
  /** The cycle's pictures, after the main one. */
  private readonly cycle: IdleMesh[] = [];
  readonly slot?: number;
  /** Greys it with its locked building. */
  private grey: ColorMatrixFilter | null = null;

  constructor(
    private readonly e: IdleElement,
    frame: (name: string) => Texture,
    glow: Texture,
    /** Reduced motion: shown at rest, as painted. */
    private readonly still = false,
  ) {
    this.root.label = `ambient:${e.id}`;
    this.area = e.area;
    this.minZoom = e.minZoom;
    this.slot = e.slot;
    this.root.position.set(e.x, e.y);
    if (e.shadow) {
      const shadow = new Sprite(glow);
      shadow.anchor.set(0.5);
      shadow.tint = 0x2a3a10;
      shadow.position.set(e.shadow.x, e.shadow.y);
      shadow.width = e.shadow.w;
      shadow.height = e.shadow.h;
      shadow.alpha = e.shadow.alpha;
      this.root.addChild(shadow);
    }
    const tex = frame(e.frame);
    const k = e.scale ?? 1;
    this.body.scale.set(e.flip ? -k : k, k);
    this.body.x = e.flip ? tex.width * k : 0;
    this.root.addChild(this.body);
    const tint = e.tint ? parseInt(e.tint.replace('#', ''), 16) : null;
    this.main = new IdleMesh(tex, e);
    if (tint !== null) this.main.mesh.tint = tint;
    this.body.addChild(this.main.mesh);
    if (e.pose) {
      const joints: Joint[] = [...(e.pose.joints ?? []), ...(e.joints ?? [])];
      this.pose = new IdleMesh(frame(e.pose.frame), { joints, breathe: e.breathe });
      this.pose.mesh.visible = false;
      if (tint !== null) this.pose.mesh.tint = tint;
      this.body.addChild(this.pose.mesh);
    }
    for (const name of e.cycle?.frames ?? []) {
      const m = new IdleMesh(frame(name), e);
      m.mesh.visible = false;
      if (tint !== null) m.mesh.tint = tint;
      this.body.addChild(m.mesh);
      this.cycle.push(m);
    }
  }

  update(t: number): void {
    if (this.still) {
      this.settle();
      return;
    }
    this.body.y = idleBob(this.e, t);
    if (this.e.cycle && this.cycle.length) {
      // The picture shown, and the next fading in over it.
      const { lo, hi, mix } = cycleAt(this.e.cycle, t);
      [this.main, ...this.cycle].forEach((m, k) => {
        const on = k === lo || (k === hi && mix > 0.002);
        m.mesh.visible = on;
        m.mesh.alpha = k === hi ? mix : 1;
        if (on) m.update(t);
      });
    } else {
      this.main.update(t);
    }
    if (this.pose && this.e.pose) {
      const level = poseLevel(this.e.pose, t);
      this.pose.mesh.visible = level > 0;
      this.pose.mesh.alpha = level;
      if (level > 0) this.pose.update(t);
    }
  }

  /** Grey and dim, as its building is while locked (the dragon on its
   *  hoard), colour coming back as the building opens. */
  setLocked(level: number): void {
    if (level <= 0.001) {
      this.body.filters = null;
      return;
    }
    if (!this.grey) {
      this.grey = new ColorMatrixFilter();
      // At the screen's own resolution: a filter draws at 1x by default.
      this.grey.resolution = 'inherit';
    }
    this.grey.matrix = lockedMatrix(level) as ColorMatrixFilter['matrix'];
    this.body.filters = [this.grey];
  }

  settle(): void {
    this.body.y = 0;
    this.main.settle();
    this.main.mesh.visible = true;
    this.main.mesh.alpha = 1;
    for (const m of this.cycle) m.mesh.visible = false;
    if (this.pose) {
      this.pose.settle();
      this.pose.mesh.visible = false;
    }
  }

  destroy(): void {
    this.root.destroy({ children: true });
    this.grey?.destroy();
  }
}

/** Butterflies over the meadows and the flower beds. */
class FlitRunner implements Runner {
  readonly root = new Container();
  readonly area: Rect;
  readonly minZoom?: number;
  readonly slot?: number;
  private readonly pool: Sprite[] = [];

  constructor(
    private readonly e: FlitElement,
    private readonly frame: (name: string) => Texture,
  ) {
    this.root.label = `ambient:${e.id}`;
    this.area = e.area;
    this.minZoom = e.minZoom;
    this.slot = e.slot;
  }

  setLocked(level: number): void {
    fadeLocked(this.root, level);
  }

  update(t: number): void {
    const flits = flitsAt(this.e, t);
    flits.forEach((f, i) => {
      let s = this.pool[i];
      if (!s) {
        s = new Sprite(this.frame(this.e.frames[f.frame] ?? ''));
        s.anchor.set(0.5);
        this.root.addChild(s);
        this.pool.push(s);
      }
      s.visible = true;
      s.position.set(f.x, f.y);
      s.rotation = f.rotation;
      s.scale.set(this.e.scale * f.open, this.e.scale);
    });
    for (let i = flits.length; i < this.pool.length; i++) this.pool[i]!.visible = false;
  }

  settle(): void {
    for (const s of this.pool) s.visible = false;
  }

  destroy(): void {
    this.root.destroy({ children: true });
  }
}

/** Little stars twinkling and drifting up. */
class SparkleRunner implements Runner {
  readonly root = new Container();
  readonly area: Rect;
  readonly minZoom?: number;
  readonly slot?: number;
  private readonly pool: Sprite[] = [];
  private readonly tint: number;

  constructor(
    private readonly e: SparkleElement,
    private readonly star: Texture,
  ) {
    this.root.label = `ambient:${e.id}`;
    this.area = e.area;
    this.minZoom = e.minZoom;
    this.slot = e.slot;
    this.tint = parseInt(e.colour.replace('#', ''), 16);
  }

  setLocked(level: number): void {
    fadeLocked(this.root, level);
  }

  update(t: number): void {
    const sparkles = sparklesAt(this.e, t);
    sparkles.forEach((k, i) => {
      let s = this.pool[i];
      if (!s) {
        s = new Sprite(this.star);
        s.anchor.set(0.5);
        s.tint = this.tint;
        s.blendMode = 'add';
        this.root.addChild(s);
        this.pool.push(s);
      }
      s.visible = true;
      s.position.set(k.x, k.y);
      s.rotation = k.rotation;
      s.scale.set(k.size / this.star.width);
      s.alpha = k.alpha;
    });
    for (let i = sparkles.length; i < this.pool.length; i++) this.pool[i]!.visible = false;
  }

  settle(): void {
    for (const s of this.pool) s.visible = false;
  }

  destroy(): void {
    this.root.destroy({ children: true });
  }
}

/** A drop of water, longer than it's wide and soft-edged: spray. */
function dropTexture(): Texture {
  const w = 32;
  const h = 14;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  ctx.translate(w / 2, h / 2);
  ctx.scale(w / 2, h / 2);
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, 1);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.5, 'rgba(240,250,255,0.7)');
  g.addColorStop(1, 'rgba(230,245,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(-1, -1, 2, 2);
  return Texture.from(canvas);
}

/** A small white wave stroke, like the painting's own: the sea's drifting
 *  highlights on slow devices. Drawn in code (no download). */
function strokeTexture(): Texture {
  const w = 64;
  const h = 24;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  ctx.lineCap = 'round';
  const curve = (x0: number, x1: number, y: number, lift: number): void => {
    ctx.beginPath();
    ctx.moveTo(x0, y);
    ctx.quadraticCurveTo((x0 + x1) / 2, y - lift, x1, y + 1);
    ctx.stroke();
  };
  // Soft first, then the bright core.
  for (const [width, alpha] of [
    [7, 0.18],
    [4.5, 0.45],
    [2.5, 1],
  ] as const) {
    ctx.lineWidth = width;
    ctx.strokeStyle = `rgba(255,255,255,${alpha})`;
    curve(8, 44, 14, 9);
    curve(36, 58, 16, 5);
  }
  return Texture.from(canvas);
}

/** A little four-pointed star with a soft glow: sparkles. Drawn in code. */
function starTexture(): Texture {
  const n = 32;
  const canvas = document.createElement('canvas');
  canvas.width = n;
  canvas.height = n;
  const ctx = canvas.getContext('2d')!;
  const c = n / 2;
  const glow = ctx.createRadialGradient(c, c, 0, c, c, c);
  glow.addColorStop(0, 'rgba(255,255,255,0.9)');
  glow.addColorStop(0.25, 'rgba(255,255,255,0.35)');
  glow.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, n, n);
  ctx.fillStyle = 'rgba(255,255,255,1)';
  ctx.beginPath();
  for (let i = 0; i < 8; i++) {
    const r = i % 2 === 0 ? c * 0.95 : c * 0.16;
    const a = (Math.PI / 4) * i - Math.PI / 2;
    const x = c + Math.cos(a) * r;
    const y = c + Math.sin(a) * r;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fill();
  return Texture.from(canvas);
}
